import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, Phone, X, Mail, Users, Landmark,
  BadgeCheck, Receipt, Instagram, Facebook, Youtube, Linkedin,
  ShieldCheck, Lock, Home,
} from 'lucide-react';
import Logo from '../components/Logo';
import StructuredData from '../components/StructuredData';
import { company, areas, testimonials, gallery, faqs, helpStats, googleReviewsUrl, socials } from '../mock';

// TikTok isn't in lucide-react, so provide a small inline mark.
const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.5 3c.3 2.1 1.5 3.6 3.5 3.9v2.5c-1.3.1-2.5-.3-3.6-1v6.4c0 3.3-2.5 5.7-5.6 5.7A5.4 5.4 0 0 1 5.4 15c0-3 2.3-5.4 5.4-5.4.3 0 .6 0 .9.1v2.7c-.3-.1-.6-.2-.9-.2-1.5 0-2.7 1.2-2.7 2.8 0 1.5 1.2 2.7 2.7 2.7 1.5 0 2.8-1.2 2.8-2.9V3h2.9z" />
  </svg>
);
const socialIconMap = { Instagram, Facebook, Youtube, Linkedin, TikTok: TikTokIcon };
const SocialIcon = ({ name, size = 18 }) => {
  const C = socialIconMap[name];
  return C ? <C size={size} /> : null;
};

const iconMap = { Users, Landmark, BadgeCheck, Receipt, ShieldCheck, Lock, Home };
const Ic = ({ name, ...rest }) => {
  const C = iconMap[name];
  return C ? <C {...rest} /> : null;
};

export function Financing() {
  return (
    <section className="finance">
      <div className="wrap">
        <div className="fcard">
          <div>
            <span className="ic-sq"><Mail size={24} strokeWidth={1.4} /></span>
            <h2>Let&rsquo;s build something great together</h2>
            <p>For more information or to set up services, don&rsquo;t hesitate to reach out. Send us a message or give us a call &mdash; we&rsquo;ll be in touch shortly.</p>
          </div>
          <div className="fbtns">
            <a className="btn btn-gold" href={company.phoneHref}>Call Us Today</a>
            <a className="btn btn-outline" href={`mailto:${company.email}`}>Email Us</a>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Areas() {
  return (
    <section className="areas" id="areas">
      <div className="wrap">
        <div className="areas-grid">
          <div>
            <p className="eyebrow">Where We Work</p>
            <h2>Serving the Savannah Lowcountry</h2>
            <p className="lede">From the Historic District's century-old cast iron to new construction on the islands, our trucks are on the road across Chatham County every day.</p>
            <Link className="btn btn-outline-dark" to="/service-areas" style={{ marginTop: '8px' }}>Check Your Address</Link>
          </div>
          <div className="area-chip-grid">
            {areas.map((a) => (
              <div className="area-chip" key={a}><span className="dot" />{a}</div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const GoogleG = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export function Credentials() {
  const items = [
    { icon: 'ShieldCheck', t: 'Class II Master Plumber', s: company.licenseNumber },
    { icon: 'BadgeCheck', t: 'GBPAT Certified', s: 'Backflow prevention' },
    { icon: 'Lock', t: 'Licensed & Insured', s: 'Fully covered' },
    { icon: 'Home', t: 'Family Owned', s: `Since ${company.founded}` },
  ];
  return (
    <section className="creds" data-testid="credentials-strip">
      <div className="wrap">
        {items.map((c) => (
          <div className="cred" key={c.t}>
            <span className="cred-ic"><Ic name={c.icon} size={22} strokeWidth={1.5} /></span>
            <div>
              <div className="cred-t">{c.t}</div>
              <div className="cred-s">{c.s}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function useLiveReviews() {
  const [live, setLive] = useState(null);
  useEffect(() => {
    let active = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d) setLive(d); })
      .catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch live reviews once on mount
  }, []);
  return live;
}

function ReviewCard({ review }) {
  const initial = (review.who || '?').trim().charAt(0).toUpperCase();
  return (
    <div className="testi-card">
      <div className="testi-top">
        <div className="stars">{'\u2605'.repeat(Math.round(review.stars || 5))}</div>
        <GoogleG size={20} />
      </div>
      <p className="quote">{review.quote}</p>
      <div className="testi-author">
        {review.photo
          ? <img className="testi-avatar" src={review.photo} alt={review.who} loading="lazy" referrerPolicy="no-referrer" />
          : <span className="testi-avatar testi-avatar-fallback" aria-hidden="true">{initial}</span>}
        <div className="testi-author-text">
          <div className="who">{review.who}</div>
          <div className="role">{review.role}</div>
        </div>
      </div>
    </div>
  );
}

export function Testimonials({ full = false }) {
  const live = useLiveReviews();
  const rating = live?.rating ? Number(live.rating).toFixed(1) : '4.9';
  const count = live?.count;
  const reviewsUrl = live?.google_maps_uri || googleReviewsUrl;

  const liveCards = (live?.reviews && live.reviews.length) ? live.reviews : [];
  const seen = new Set();
  const pool = [...liveCards, ...testimonials]
    .filter((r) => Math.round(r.stars || 5) >= 5)
    .filter((r) => {
      const k = `${r.who || ''}|${(r.quote || '').slice(0, 24)}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  const cards = pool.length ? pool : (liveCards.length ? liveCards : testimonials);

  const [start, setStart] = useState(0);
  useEffect(() => {
    if (full || cards.length <= 3) return undefined;
    const id = setInterval(() => setStart((s) => (s + 3) % cards.length), 6000);
    return () => clearInterval(id);
  }, [full, cards.length]);

  const visible = full
    ? cards
    : Array.from({ length: Math.min(3, cards.length) }, (_, i) => cards[(start + i) % cards.length]);

  return (
    <section className="testimonials on-dark" id="reviews">
      <div className="wrap">
        <div className="sec-head">
          {!full && (
            <>
              <p className="eyebrow">Customer Reviews</p>
              <h2 style={{ color: 'var(--ivory)' }}>What our customers say</h2>
            </>
          )}
          <div className="google-badge" data-testid="google-reviews-widget">
            <GoogleG size={30} />
            <div className="gb-text">
              <span className="gb-rating" data-testid="reviews-rating">{rating}</span>
              <span className="gb-stars" aria-label={`${rating} out of 5 stars`}>&#9733;&#9733;&#9733;&#9733;&#9733;</span>
            </div>
            <span className="gb-label" data-testid="reviews-count">
              {count ? `Based on ${count} Google reviews` : 'Based on Google reviews'}
            </span>
          </div>
          <a className="btn btn-outline" style={{ color: '#ffffff' }} href={reviewsUrl} target="_blank" rel="noopener noreferrer">Read Our Reviews on Google</a>
        </div>
        <div className="testi-grid" key={full ? 'all' : start} data-testid="reviews-grid">
          {visible.map((t, i) => (
            <ReviewCard review={t} key={`${t.who || 'review'}-${start}-${i}`} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function Gallery({ full = false }) {
  const [active, setActive] = useState(null);
  return (
    <section className="gallery" id="gallery">
      <div className="wrap">
        {!full && (
        <div className="sec-head">
          <p className="eyebrow">Our Work</p>
          <h2>See our work in action</h2>
          <p>From residential repairs to commercial installations, take a look at some of the projects we've completed across the Lowcountry.</p>
        </div>
        )}
        <div className="gal-grid">
          {gallery.map((g) => (
            <div className="gal-item" key={g.img} onClick={() => setActive(g)}>
              <span className="tag">{g.tag}</span>
              <img src={g.img} alt={g.title} loading="lazy" />
              <div className="ov"><span>{g.title}</span></div>
            </div>
          ))}
        </div>
        {!full && (
        <div className="gal-actions">
          <Link className="btn btn-outline-dark" to="/gallery">View Full Gallery</Link>
        </div>
        )}
      </div>
      {active && (
        <div className="lightbox" onClick={() => setActive(null)}>
          <button className="lb-close" onClick={() => setActive(null)}><X size={16} /></button>
          <img src={active.img} alt={active.title} onClick={(e) => e.stopPropagation()} />
          <div className="lb-cap">{active.title}</div>
        </div>
      )}
    </section>
  );
}

export function FinalCTA() {
  return (
    <section className="final-cta">
      <div className="wrap">
        <div className="inner">
          <div>
            <p className="eyebrow" style={{ color: 'var(--gold-light)' }}>Ready to Get Started?</p>
            <h2>Plumbing you can trust, done right the first time</h2>
            <ul>
              <li><span className="ic"><CheckCircle2 size={18} /></span>Customer-centered, honest service</li>
              <li><span className="ic"><CheckCircle2 size={18} /></span>Quality materials built to last</li>
              <li><span className="ic"><CheckCircle2 size={18} /></span>Permits &amp; inspections handled</li>
            </ul>
            <div className="help-actions">
              <a className="btn btn-gold" href={company.phoneHref}><Phone size={16} /> Call Now</a>
              <a className="btn btn-outline" href={`mailto:${company.email}`}><Mail size={16} /> Email Us</a>
            </div>
          </div>
          <div className="help-stats">
            {helpStats.map((h) => (
              <div className="help-stat" key={h.t}>
                {h.k ? <div className="k">{h.k}</div> : <div className="ic"><Ic name={h.icon} size={24} strokeWidth={1.4} /></div>}
                <div className="t">{h.t}</div>
                <div className="s">{h.s}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function FaqAccordion({ items }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="faq-grid">
      {items.map((f, i) => {
        const isOpen = open === i;
        return (
          <div className={`faq-pill ${isOpen ? 'open' : ''}`} key={f.q}>
            <button
              className="faq-q"
              onClick={() => setOpen(isOpen ? -1 : i)}
              aria-expanded={isOpen}
              aria-controls={`faq-answer-${i}`}
              data-testid={`faq-question-${i}`}
            >
              {f.q}<span className="plus" aria-hidden="true">+</span>
            </button>
            <div className="faq-a" id={`faq-answer-${i}`} role="region" data-testid={`faq-answer-${i}`}><div className="inner">{f.a}</div></div>
          </div>
        );
      })}
    </div>
  );
}

export function PageFaq({ eyebrow = 'Common Questions', title, subtitle, items, testid, viewAll = false }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="faq-full" data-testid={testid}>
      <StructuredData faqItems={items} />
      <div className="wrap">
        <div className="sec-head" style={{ marginBottom: '28px' }}>
          <p className="eyebrow">{eyebrow}</p>
          <h2>{title}</h2>
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
        <FaqAccordion items={items} />
        {viewAll && (
          <div className="faq-actions">
            <Link className="btn btn-outline-dark" to="/faq" data-testid={testid ? `${testid}-view-all` : 'faq-view-all'}>View All FAQs</Link>
          </div>
        )}
      </div>
    </section>
  );
}

export function FAQ() {
  return (
    <section id="faq">
      <div className="wrap">
        <div className="sec-head">
          <p className="eyebrow">Common Questions</p>
          <h2>Answers before you call</h2>
          <p>Quick answers to frequently asked questions about our services.</p>
        </div>
        <FaqAccordion items={faqs.slice(0, 6)} />
        <div className="faq-actions">
          <Link className="btn btn-outline-dark" to="/faq">View All FAQs</Link>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="site" id="contact">
      <div className="ridge" />
      <div className="foot-top">
        <div className="wrap">
          <div className="foot-grid">
            <div>
              <div className="foot-brand">
                <Logo size={48} />
                <div className="name">{company.name}<span>{company.location}</span></div>
              </div>
              <p>Family owned and operated since {company.founded}. Your expert Savannah plumber, built on integrity and high standards for the Coastal Empire.</p>
              <div className="foot-social">
                {socials.map((s) => (
                  <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.name} title={s.name}>
                    <SocialIcon name={s.icon} size={18} />
                  </a>
                ))}
              </div>
            </div>
            <div className="foot-col">
              <h5>Services</h5>
              <ul>
                <li><Link to="/residential">Residential</Link></li>
                <li><Link to="/commercial">Commercial</Link></li>
              </ul>
            </div>
            <div className="foot-col">
              <h5>Company</h5>
              <ul>
                <li><Link to="/about">About Us</Link></li>
                <li><Link to="/team">Our Team</Link></li>
                <li><Link to="/gallery">Gallery</Link></li>
                <li><Link to="/reviews">Reviews</Link></li>
                <li><Link to="/careers">We're Hiring</Link></li>
                <li><a href="/#contact">Contact</a></li>
              </ul>
            </div>
            <div className="foot-col">
              <h5>Contact Us</h5>
              <ul>
                <li><a href={company.phoneHref}>{company.phoneDisplay}</a></li>
                <li><a href={`mailto:${company.email}`}>{company.email}</a></li>
                <li>{company.address}</li>
                <li>{company.hours}</li>
                <li>{company.hoursNote}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="foot-bottom">
        <div className="wrap">
          <div>&copy; 2026 {company.name}. All rights reserved.</div>
          <div>{company.license} &middot; {company.licenseNumber} &middot; GBPAT Certified &middot; Family Owned &amp; Operated &middot; Savannah, GA</div>
        </div>
      </div>
    </footer>
  );
}
